import React, { useState, useCallback, useMemo } from 'react';
import {
  List,
  Button,
  Modal,
  Checkbox,
  Space,
  Typography,
  Breadcrumb,
  Popconfirm,
  message,
  Image,
  Card,
  Row,
  Col,
  Empty,
  Spin,
  Input,
  Tooltip,
  Drawer,
} from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import {
  FolderOutlined,
  FileOutlined,
  UploadOutlined,
  DeleteOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  SearchOutlined,
  EyeOutlined,
  DownloadOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload';
import { useTranslation } from 'react-i18next';
import FileUploadField from './FileUploadField';
import { MediaUploadConfig, isZipFile } from '../../utils/file.util';
import { useMedia, type MediaFile, type ZipUploadResponse } from '../../hooks/use-media.hook';
import { useActiveTenant } from '../../states/active-tenant.atom';

const { Title, Text } = Typography;
const { Search } = Input;

interface DirectoryStructure {
  files: MediaFile[];
  directories: MediaFile[];
}

export interface MediaPickerProps {
  // Configuration
  uploadConfig: MediaUploadConfig;
  multiple?: boolean;
  showPreview?: boolean;
  showDownload?: boolean;
  allowDirectoryCreation?: boolean;

  // Callbacks
  onFileSelect?: (file: MediaFile) => void;
  onFilesSelect?: (files: MediaFile[]) => void;
  onError?: (error: string) => void;

  // Styling
  className?: string;
  height?: number;
}

export interface MediaPickerTriggerProps extends Omit<MediaPickerProps, 'className' | 'height'> {
  buttonProps?: {
    type?: 'link' | 'text' | 'default' | 'primary' | 'dashed';
    size?: 'small' | 'middle' | 'large';
    className?: string;
    style?: React.CSSProperties;
  };
  drawerProps?: {
    width?: number | string;
    placement?: 'left' | 'right' | 'top' | 'bottom';
    className?: string;
    style?: React.CSSProperties;
  };
}

const MediaPickerTrigger: React.FC<MediaPickerTriggerProps> = ({
  buttonProps = {},
  drawerProps = {},
  onFileSelect,
  onFilesSelect,
  ...mediaPickerProps
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleFileSelectWithClose = (file: MediaFile) => {
    if (onFileSelect) {
      onFileSelect(file);
    }
    handleClose();
  };

  const handleFilesSelectWithClose = (files: MediaFile[]) => {
    if (onFilesSelect) {
      onFilesSelect(files);
    }
    handleClose();
  };

  return (
    <>
      <Button
        icon={<PictureOutlined />}
        onClick={() => setIsOpen(true)}
        type="default"
        {...buttonProps}
      >
        {t('common.mediaPicker.title')}
      </Button>
      <Drawer
        title={t('common.mediaPicker.title')}
        open={isOpen}
        onClose={handleClose}
        width={800}
        styles={{ body: { padding: 0 } }}
        {...drawerProps}
      >
        <MediaPicker
          {...mediaPickerProps}
          height={window.innerHeight - 110}
          onFileSelect={handleFileSelectWithClose}
          onFilesSelect={handleFilesSelectWithClose}
        />
      </Drawer>
    </>
  );
};

export const MediaPicker: React.FC<MediaPickerProps> = ({
  uploadConfig,
  multiple = false,
  showPreview = true,
  showDownload = false,
  allowDirectoryCreation = true,
  onFileSelect,
  onFilesSelect,
  onError,
  className,
  height = 600,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Initialize media hooks
  const {
    useListFiles,
    useUploadFiles,
    useDeleteFiles,
    useCreateDirectory,
    useDeleteDirectory,
    useUploadZip,
  } = useMedia();
  const { activeTenant } = useActiveTenant();

  // State management
  const [currentPath, setCurrentPath] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [selectedDirectory, setSelectedDirectory] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [createDirModalVisible, setCreateDirModalVisible] = useState(false);
  const [newDirName, setNewDirName] = useState('');
  const [uploadFiles, setUploadFiles] = useState<RcFile | RcFile[] | null>(null);

  // Query and mutations
  // Convert S3 path to relative path for API calls
  const getApiPath = useCallback(
    (path: string) => {
      if (!activeTenant) return path;

      let relativePath = '';

      // If it's an S3 path (starts with media/tenantId/), extract the relative part
      if (path.startsWith(`media/${activeTenant.id}/`)) {
        relativePath = path.substring(`media/${activeTenant.id}/`.length);
      }
      // If it's a file storage path (starts with tenantId/), extract the relative part
      else if (path.startsWith(`${activeTenant.id}/`)) {
        relativePath = path.substring(`${activeTenant.id}/`.length);
      }
      // Otherwise, return as is
      else {
        return path;
      }

      // Remove trailing slash if present
      if (relativePath.endsWith('/')) {
        relativePath = relativePath.slice(0, -1);
      }

      console.log('getApiPath conversion:', {
        input: path,
        tenantId: activeTenant.id,
        relativePath,
        finalResult: relativePath,
      });

      return relativePath;
    },
    [activeTenant]
  );

  const { data: mediaResponse, isLoading: isLoadingFiles } = useListFiles(getApiPath(currentPath));

  // Organize files into directory structure
  const { currentFiles, currentDirectories } = useMemo(() => {
    if (!mediaResponse?.data || !activeTenant) {
      return { currentFiles: [], currentDirectories: [] };
    }

    const files: MediaFile[] = [];
    const directories: MediaFile[] = [];
    const tenantId = activeTenant.id;

    // Debug logging
    console.log('MediaPicker Debug:', {
      currentPath,
      apiPath: getApiPath(currentPath),
      tenantId,
      dataLength: mediaResponse.data.length,
      data: mediaResponse.data,
    });

    // Additional debugging for path conversion
    if (currentPath) {
      console.log('Path conversion debug:', {
        originalPath: currentPath,
        isS3Path: currentPath.startsWith(`media/${tenantId}/`),
        isFileStoragePath: currentPath.startsWith(`${tenantId}/`),
        convertedPath: getApiPath(currentPath),
        expectedApiCall: `/v1/admin/tenants/${tenantId}/media/${getApiPath(currentPath)}`,
      });
    }

    // Process each item
    mediaResponse.data.forEach((item) => {
      // Check if item is a directory (works for both file storage and S3)
      const isDirectory = item.isDirectory || (item.directoryUrl && item.size === 0);

      // If we're at root, show only items inside tenant's directory
      if (currentPath === '') {
        // For S3 storage, check if the key starts with the tenant's media path
        // For file storage, check if the key starts with tenant ID
        const isInTenantDirectory =
          item.key.startsWith(`media/${tenantId}/`) || item.key.startsWith(`${tenantId}/`);

        if (isInTenantDirectory) {
          let relativePath = '';
          if (item.key.startsWith(`media/${tenantId}/`)) {
            // S3 storage format: media/tenantId/path
            relativePath = item.key.substring(`media/${tenantId}/`.length);
            // Remove trailing slash for S3 directories
            if (relativePath.endsWith('/')) {
              relativePath = relativePath.slice(0, -1);
            }
          } else {
            // File storage format: tenantId/path
            relativePath = item.key.substring(tenantId.length + 1);
          }

          // Only show immediate children of tenant's directory
          if (!relativePath.includes('/')) {
            if (isDirectory) {
              directories.push(item);
            } else {
              files.push(item);
            }
          }
        }
      }
      // If we're in a directory, show items in that directory
      else {
        // For S3 storage, check if the item is in the current directory
        // For file storage, use the existing logic
        let isInCurrentDirectory = false;

        if (item.key.startsWith(`media/${tenantId}/`)) {
          // S3 storage: check if item is in current directory
          const currentDirPath = currentPath.startsWith(`media/${tenantId}/`)
            ? currentPath
            : `media/${tenantId}/${currentPath}`;

          // Ensure currentDirPath ends with '/' for proper matching
          const normalizedCurrentPath = currentDirPath.endsWith('/')
            ? currentDirPath
            : currentDirPath + '/';

          // Check if item is a direct child of the current directory
          if (item.key.startsWith(normalizedCurrentPath)) {
            const remainingPath = item.key.substring(normalizedCurrentPath.length);
            // Remove trailing slash if present and split
            const pathSegments = remainingPath.replace(/\/$/, '').split('/').filter(Boolean);
            // Item is in current directory if it has exactly one path segment (direct child)
            isInCurrentDirectory = pathSegments.length === 1;
          }
        } else {
          // File storage: use existing logic
          const itemPath = item.key.substring(0, item.key.lastIndexOf('/') || item.key.length);
          isInCurrentDirectory = itemPath === currentPath;
        }

        if (isInCurrentDirectory) {
          if (isDirectory) {
            directories.push(item);
          } else {
            files.push(item);
          }
        }
      }
    });

    return {
      currentFiles: files,
      currentDirectories: directories,
    };
  }, [mediaResponse?.data, currentPath, activeTenant, getApiPath]);

  const { mutateAsync: uploadFilesMutation, status: uploadStatus } = useUploadFiles({
    onSuccess: () => {
      message.success(t('common.mediaPicker.uploadSuccess'));
      setUploadModalVisible(false);
      setUploadFiles(null);
    },
    onError: (error: any) => {
      // Prevent error from being thrown as uncaught
      console.error('File upload error:', error);

      // Extract error message from axios error
      let errorMsg = 'Upload failed';
      if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error?.response?.data?.data?.message) {
        errorMsg = error.response.data.data.message;
      } else if (error instanceof Error) {
        errorMsg = error.message;
      } else if (typeof error === 'string') {
        errorMsg = error;
      }

      message.error(errorMsg);
      if (onError) onError(errorMsg);
    },
  });

  const { mutateAsync: uploadZipMutation, status: zipUploadStatus } = useUploadZip({
    onSuccess: (response: ZipUploadResponse) => {
      if (response.success) {
        // Invalidate and refetch media list queries to refresh the file list
        queryClient.invalidateQueries({ queryKey: ['media', 'list'] });
        // Refetch the current path's file list immediately
        queryClient.refetchQueries({ queryKey: ['media', 'list', getApiPath(currentPath)] });
        message.success(t('fileManager.zipSuccess'));
        setUploadModalVisible(false);
        setUploadFiles(null);
      } else {
        // Show validation errors if any
        if (response.validationErrors && response.validationErrors.length > 0) {
          const errorList = response.validationErrors
            .map((err) => `${err.file}: ${err.error}`)
            .join('\n');
          message.error({
            content: (
              <div>
                <div>{t('fileManager.zipValidationFailed')}</div>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', marginTop: '8px' }}>
                  {errorList}
                </pre>
              </div>
            ),
            duration: 10,
          });
        } else {
          message.error(response.message || t('fileManager.zipValidationFailed'));
        }
        if (onError) onError(response.message || 'Zip upload failed');
      }
    },
    onError: (error: any) => {
      // Prevent error from being thrown as uncaught
      console.error('Zip upload error:', error);

      // Handle 400 errors that contain validation errors in the response
      if (error?.response?.status === 400) {
        const responseData = error.response?.data;
        // Backend wraps response in data.data
        const zipResponse = responseData?.data || responseData;

        if (zipResponse?.validationErrors && Array.isArray(zipResponse.validationErrors)) {
          const errorList = zipResponse.validationErrors
            .map((err: { file: string; error: string }) => `${err.file}: ${err.error}`)
            .join('\n');
          message.error({
            content: (
              <div>
                <div>{t('fileManager.zipValidationFailed')}</div>
                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', marginTop: '8px' }}>
                  {errorList}
                </pre>
              </div>
            ),
            duration: 10,
          });
          if (onError) {
            onError(zipResponse.message || 'Zip upload validation failed');
          }
          return;
        }

        // If there's a message in the response, show it
        if (zipResponse?.message) {
          message.error(zipResponse.message);
          if (onError) onError(zipResponse.message);
          return;
        }

        // Extract error message from response
        const errorMessage =
          zipResponse?.message ||
          responseData?.message ||
          error.response?.data?.message ||
          'Zip upload failed. Please check the file and try again.';
        message.error(errorMessage);
        if (onError) onError(errorMessage);
        return;
      }

      // Extract error message from axios error
      let errorMsg = 'Zip upload failed';
      if (error?.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error?.response?.data?.data?.message) {
        errorMsg = error.response.data.data.message;
      } else if (error instanceof Error) {
        errorMsg = error.message;
      } else if (typeof error === 'string') {
        errorMsg = error;
      }

      message.error(errorMsg);
      if (onError) onError(errorMsg);
    },
  });

  const { mutateAsync: deleteFilesMutation, status: deleteStatus } = useDeleteFiles({
    onSuccess: () => {
      message.success(t('common.mediaPicker.deleteSuccess'));
      setSelectedFiles([]);
    },
    onError: (error: unknown) => {
      const errorMsg = error instanceof Error ? error.message : 'Delete failed';
      if (onError) onError(errorMsg);
      else message.error(errorMsg);
    },
  });

  const { mutateAsync: deleteDirectoryMutation, status: deleteDirectoryStatus } =
    useDeleteDirectory({
      onSuccess: () => {
        message.success(t('common.mediaPicker.deleteDirectorySuccess'));
        setSelectedDirectory('');
        // If we're in the directory that was deleted, go back
        if (currentPath === selectedDirectory) {
          handleBackClick();
        }
      },
      onError: (error: unknown) => {
        const errorMsg = error instanceof Error ? error.message : 'Failed to delete directory';
        if (onError) onError(errorMsg);
        else message.error(errorMsg);
      },
    });

  const { mutateAsync: createDirectoryMutation, status: createDirStatus } = useCreateDirectory({
    onSuccess: () => {
      message.success(t('common.mediaPicker.createDirectorySuccess'));
      setCreateDirModalVisible(false);
      setNewDirName('');
    },
    onError: (error: unknown) => {
      const errorMsg = error instanceof Error ? error.message : 'Failed to create directory';
      if (onError) onError(errorMsg);
      else message.error(errorMsg);
    },
  });

  // Utility functions
  const sanitizeName = useCallback((name: string): string => {
    // Replace spaces and other problematic characters with underscores
    // This prevents URL encoding issues with S3
    return name.replace(/\s+/g, '_').replace(/[^\w.-]/g, '_');
  }, []);

  const normalizePath = useCallback((path: string, tenantId: string) => {
    // Normalize path to handle both file storage and S3 storage formats
    if (path.startsWith(`media/${tenantId}/`)) {
      // S3 storage format - ensure it ends with '/' for directories
      return path.endsWith('/') ? path : path + '/';
    } else if (path.startsWith(tenantId)) {
      // File storage format - keep as is
      return path;
    } else {
      // If path doesn't start with tenant info, assume it's relative to tenant
      return `${tenantId}/${path}`;
    }
  }, []);

  const getPathParts = useCallback(() => {
    if (!currentPath || !activeTenant) return [];
    const parts = currentPath.split('/').filter(Boolean);

    // Check if we're using S3 storage format (media/tenantId/...)
    const isS3Format = currentPath.startsWith(`media/${activeTenant.id}/`);

    if (isS3Format) {
      // S3 storage: remove media/tenantId from the display path
      if (parts.length >= 2 && parts[0] === 'media' && parts[1] === activeTenant.id) {
        return parts.slice(2);
      }
      return parts;
    } else {
      // File storage: if the first part is the tenant ID, remove it from the display path
      if (parts[0] === activeTenant.id) {
        return parts.slice(1);
      }
      return parts;
    }
  }, [currentPath, activeTenant]);

  const getFileExtension = (fileName: string) => {
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  const getItemName = (item: MediaFile) => {
    // Remove trailing slash if present, then get the last part
    const cleanKey = item.key.endsWith('/') ? item.key.slice(0, -1) : item.key;
    return cleanKey.split('/').pop() || '';
  };

  const isImageFile = (file: MediaFile) => {
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(getFileExtension(file.key));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Event handlers
  const handleDirectoryClick = (directory: MediaFile) => {
    // Use the key directly for navigation (both file storage and S3)
    // The key contains the full path needed for the next API call
    setCurrentPath(directory.key);
    setSelectedFiles([]);
    setSelectedDirectory('');
  };

  const handleDirectorySelect = (directory: MediaFile) => {
    setSelectedDirectory(selectedDirectory === directory.key ? '' : directory.key);
  };

  const handleBackClick = () => {
    if (!activeTenant) return;

    const parts = currentPath.split('/').filter(Boolean);

    // Check if we're using S3 storage format (media/tenantId/...)
    const isS3Format = currentPath.startsWith(`media/${activeTenant.id}/`);

    if (isS3Format) {
      // S3 storage: media/tenantId/path
      if (parts.length > 2) {
        // More than just media/tenantId
        const newPath = parts.slice(0, -1).join('/');
        setCurrentPath(newPath);
        setSelectedFiles([]);
        setSelectedDirectory('');
      } else if (parts.length === 2 && parts[0] === 'media' && parts[1] === activeTenant.id) {
        // We're at tenant root, don't allow going back further
        return;
      } else {
        // If somehow we're outside tenant directory, go to tenant root
        setCurrentPath(`media/${activeTenant.id}`);
        setSelectedFiles([]);
        setSelectedDirectory('');
      }
    } else {
      // File storage: tenantId/path
      if (parts.length > 1) {
        // More than just the tenant ID
        const newPath = parts.slice(0, -1).join('/');
        setCurrentPath(newPath);
        setSelectedFiles([]);
        setSelectedDirectory('');
      } else if (parts.length === 1 && parts[0] === activeTenant.id) {
        // We're at tenant root, don't allow going back further
        return;
      } else {
        // If somehow we're outside tenant directory, go to tenant root
        setCurrentPath(activeTenant.id);
        setSelectedFiles([]);
        setSelectedDirectory('');
      }
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (!activeTenant) return;

    const pathParts = getPathParts();
    const newPathParts = pathParts.slice(0, index + 1);

    // Check if we're using S3 storage format
    const isS3Format = currentPath.startsWith(`media/${activeTenant.id}/`);

    let newPath = '';
    if (isS3Format) {
      // S3 storage: reconstruct with media/tenantId prefix
      newPath = `media/${activeTenant.id}/${newPathParts.join('/')}`;
    } else {
      // File storage: reconstruct with tenantId prefix
      newPath = `${activeTenant.id}/${newPathParts.join('/')}`;
    }

    setCurrentPath(newPath);
    setSelectedFiles([]);
    setSelectedDirectory('');
  };

  const handleFileSelection = (fileKey: string, checked: boolean) => {
    if (multiple) {
      setSelectedFiles((prev) =>
        checked ? [...prev, fileKey] : prev.filter((key) => key !== fileKey)
      );
    } else {
      setSelectedFiles(checked ? [fileKey] : []);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedFiles(filteredFiles.map((file) => file.key));
    } else {
      setSelectedFiles([]);
    }
  };

  const handleConfirmSelection = () => {
    const selectedFileObjects = currentFiles.filter((file) => selectedFiles.includes(file.key));

    if (multiple && onFilesSelect) {
      onFilesSelect(selectedFileObjects);
    } else if (!multiple && onFileSelect && selectedFileObjects.length > 0) {
      onFileSelect(selectedFileObjects[0]);
    }
  };

  const handleDeleteDirectory = () => {
    if (!selectedDirectory) return;
    deleteDirectoryMutation({ path: getApiPath(selectedDirectory) });
  };

  const handleUpload = async () => {
    if (!uploadFiles) return;

    const filesToUpload = Array.isArray(uploadFiles) ? uploadFiles : [uploadFiles];

    // Check if any file is a zip file
    const hasZipFile = filesToUpload.some((file) => isZipFile(file));

    // If we have a zip file, use zip upload endpoint
    if (hasZipFile) {
      // For zip upload, we only support single zip file
      if (filesToUpload.length > 1) {
        message.warning('Please upload only one zip file at a time');
        return;
      }

      const zipFile = filesToUpload[0];
      const formData = new FormData();

      // Sanitize the filename
      const sanitizedName = sanitizeName(zipFile.name);
      const sanitizedFile = new File([zipFile], sanitizedName, { type: zipFile.type });

      formData.append('file', sanitizedFile);

      // Enable folder structure validation
      formData.append('validateFolderStructure', 'true');

      if (currentPath) {
        formData.append('path', getApiPath(currentPath));
      }

      try {
        await uploadZipMutation(formData);
      } catch (error) {
        // Error is already handled in onError callback, but we catch it here
        // to prevent uncaught promise rejection
        // The onError callback will show the error message
      }
      return;
    }

    // Regular file upload
    const formData = new FormData();

    filesToUpload.forEach((file) => {
      // Sanitize the filename to prevent issues with spaces and special characters
      const sanitizedName = sanitizeName(file.name);

      // Create a new file with the sanitized name
      const sanitizedFile = new File([file], sanitizedName, { type: file.type });

      formData.append('files', sanitizedFile);
    });

    if (currentPath) {
      formData.append('path', getApiPath(currentPath));
    }

    try {
      await uploadFilesMutation(formData);
    } catch (error) {
      // Error is already handled in onError callback, but we catch it here
      // to prevent uncaught promise rejection
      // The onError callback will show the error message
    }
  };

  const handleDelete = () => {
    if (selectedFiles.length === 0) return;
    deleteFilesMutation({ fileKeys: selectedFiles });
  };

  const handleCreateDirectory = () => {
    if (!newDirName.trim()) return;

    // Sanitize directory name to prevent issues with spaces and special characters
    const sanitizedDirName = sanitizeName(newDirName.trim());
    const newPath = currentPath ? `${currentPath}/${sanitizedDirName}` : sanitizedDirName;

    createDirectoryMutation({ path: getApiPath(newPath) });
  };

  // Filter files based on search
  const filteredFiles = currentFiles.filter((file) =>
    getItemName(file).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredDirectories = currentDirectories.filter((dir) =>
    getItemName(dir).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if uploaded files contain zip
  const hasZipFileInUpload = useMemo(() => {
    if (!uploadFiles) return false;
    const filesToCheck = Array.isArray(uploadFiles) ? uploadFiles : [uploadFiles];
    return filesToCheck.some((file) => isZipFile(file));
  }, [uploadFiles]);

  const isLoading =
    isLoadingFiles ||
    uploadStatus === 'pending' ||
    zipUploadStatus === 'pending' ||
    deleteStatus === 'pending' ||
    deleteDirectoryStatus === 'pending' ||
    createDirStatus === 'pending';

  const handlePreviewClick = (file: MediaFile) => {
    setPreviewImage(file.url);
    setPreviewVisible(true);
  };

  return (
    <div className={className} style={{ height }}>
      <Card>
        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Space>
                {/* {currentPath && (
                  <Button icon={<ArrowLeftOutlined />} onClick={handleBackClick}>
                    {t('common.back')}
                  </Button>
                )} */}
                <Title level={4} style={{ margin: 0 }}>
                  {t('common.mediaPicker.title')}
                </Title>
              </Space>
            </Col>
            <Col>
              <Space>
                {allowDirectoryCreation && (
                  <Button icon={<PlusOutlined />} onClick={() => setCreateDirModalVisible(true)}>
                    {t('common.mediaPicker.createDirectory')}
                  </Button>
                )}
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setUploadModalVisible(true)}
                >
                  {t('common.mediaPicker.upload')}
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Breadcrumb */}
          {currentPath && activeTenant && (
            <Breadcrumb style={{ marginTop: 8 }}>
              <Breadcrumb.Item>
                <Button
                  type="link"
                  onClick={() => {
                    setCurrentPath('');
                  }}
                >
                  {t('common.mediaPicker.root')}
                </Button>
              </Breadcrumb.Item>
              {getPathParts().map((part, index) => {
                // Reconstruct the full path up to this part
                const isS3Format = currentPath.startsWith(`media/${activeTenant.id}/`);
                const fullPath = isS3Format
                  ? `media/${activeTenant.id}/${getPathParts()
                      .slice(0, index + 1)
                      .join('/')}`
                  : `${activeTenant.id}/${getPathParts()
                      .slice(0, index + 1)
                      .join('/')}`;
                return (
                  <Breadcrumb.Item key={index}>
                    <Button type="link" onClick={() => setCurrentPath(fullPath)}>
                      {part}
                    </Button>
                  </Breadcrumb.Item>
                );
              })}
            </Breadcrumb>
          )}

          {/* Search */}
          <Search
            placeholder={t('common.mediaPicker.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginTop: 8 }}
            allowClear
          />
        </div>

        {/* Action Bar */}
        {(filteredFiles.length > 0 ||
          (allowDirectoryCreation && filteredDirectories.length > 0)) && (
          <div style={{ marginBottom: 16 }}>
            <Row justify="space-between" align="middle">
              <Col>
                <Space>
                  {filteredFiles.length > 0 && (
                    <>
                      <Checkbox
                        checked={selectedFiles.length === filteredFiles.length}
                        indeterminate={
                          selectedFiles.length > 0 && selectedFiles.length < filteredFiles.length
                        }
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      >
                        {t('common.mediaPicker.selectAll')}
                      </Checkbox>
                      <Text type="secondary">
                        {selectedFiles.length} {t('common.mediaPicker.selected')}
                      </Text>
                    </>
                  )}
                </Space>
              </Col>
              <Col>
                <Space>
                  {selectedFiles.length > 0 && (
                    <>
                      <Button
                        type="primary"
                        onClick={handleConfirmSelection}
                        disabled={selectedFiles.length === 0}
                      >
                        {multiple
                          ? t('common.mediaPicker.selectFiles')
                          : t('common.mediaPicker.selectFile')}
                      </Button>
                      <Popconfirm
                        title={t('common.mediaPicker.deleteConfirm')}
                        onConfirm={handleDelete}
                      >
                        <Button danger icon={<DeleteOutlined />}>
                          {t('common.mediaPicker.delete')}
                        </Button>
                      </Popconfirm>
                    </>
                  )}
                  {selectedDirectory && (
                    <Popconfirm
                      title={t('common.mediaPicker.deleteDirectoryConfirm')}
                      onConfirm={handleDeleteDirectory}
                    >
                      <Button icon={<DeleteOutlined />}>
                        {t('common.mediaPicker.deleteDirectory')}
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </Col>
            </Row>
          </div>
        )}

        {/* Content */}
        <div style={{ height: height - 200, overflowY: 'auto' }}>
          <Spin spinning={isLoading}>
            {filteredDirectories.length === 0 && filteredFiles.length === 0 ? (
              <Empty description={t('common.mediaPicker.noFiles')} />
            ) : (
              <List
                dataSource={[
                  ...filteredDirectories.map((dir) => ({ type: 'directory', ...dir })),
                  ...filteredFiles.map((file) => ({ type: 'file', ...file })),
                ]}
                renderItem={(item: any) => (
                  <List.Item
                    key={item.type === 'directory' ? `dir-${item.key}` : `file-${item.key}`}
                    actions={
                      item.type === 'file'
                        ? [
                            showPreview && isImageFile(item) && (
                              <Tooltip title={t('common.mediaPicker.preview')}>
                                <Button
                                  icon={<EyeOutlined />}
                                  onClick={() => handlePreviewClick(item)}
                                />
                              </Tooltip>
                            ),
                            showDownload && (
                              <Tooltip title={t('common.mediaPicker.download')}>
                                <Button
                                  icon={<DownloadOutlined />}
                                  onClick={() => window.open(item.url, '_blank')}
                                />
                              </Tooltip>
                            ),
                          ].filter(Boolean)
                        : [
                            allowDirectoryCreation && (
                              <Tooltip title={t('common.mediaPicker.deleteDirectory')}>
                                <Button
                                  icon={<DeleteOutlined />}
                                  onClick={() => handleDirectorySelect(item)}
                                />
                              </Tooltip>
                            ),
                          ].filter(Boolean)
                    }
                  >
                    <List.Item.Meta
                      avatar={
                        item.type === 'directory' ? (
                          <Space>
                            {allowDirectoryCreation && (
                              <Checkbox
                                checked={selectedDirectory === item.key}
                                onChange={(e) => handleDirectorySelect(item)}
                              />
                            )}
                            <FolderOutlined />
                          </Space>
                        ) : (
                          <Space>
                            <Checkbox
                              checked={selectedFiles.includes(item.key)}
                              onChange={(e) => handleFileSelection(item.key, e.target.checked)}
                            />
                            {isImageFile(item) ? (
                              <Image
                                src={item.url}
                                alt={item.key}
                                width={40}
                                height={40}
                                style={{ objectFit: 'cover' }}
                                preview={false}
                              />
                            ) : (
                              <FileOutlined style={{ fontSize: 24 }} />
                            )}
                          </Space>
                        )
                      }
                      title={
                        item.type === 'directory' ? (
                          <Button
                            type="link"
                            onClick={() => handleDirectoryClick(item)}
                            style={{ padding: 0, height: 'auto' }}
                          >
                            {getItemName(item)}
                          </Button>
                        ) : (
                          <Text>{getItemName(item)}</Text>
                        )
                      }
                      description={
                        item.type === 'file' && (
                          <Space>
                            <Text type="secondary">{formatFileSize(item.size)}</Text>
                            <Text type="secondary">
                              {new Date(item.lastModified).toLocaleDateString()}
                            </Text>
                          </Space>
                        )
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Spin>
        </div>
      </Card>

      {/* Upload Modal */}
      <Modal
        title={t('common.mediaPicker.uploadFiles')}
        open={uploadModalVisible}
        onOk={handleUpload}
        onCancel={() => {
          setUploadModalVisible(false);
          setUploadFiles(null);
        }}
        okText={t('common.mediaPicker.upload')}
        cancelText={t('common.cancel')}
        okButtonProps={{
          disabled: !uploadFiles || zipUploadStatus === 'pending' || uploadStatus === 'pending',
          loading: zipUploadStatus === 'pending' || uploadStatus === 'pending',
        }}
      >
        <FileUploadField
          value={uploadFiles}
          onChange={setUploadFiles}
          multiple={true}
          uploadConfig={uploadConfig}
          onError={onError}
        />
        {zipUploadStatus === 'pending' && (
          <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
            {t('fileManager.zipUploading')}
          </Text>
        )}
        {uploadStatus === 'pending' && zipUploadStatus !== 'pending' && (
          <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
            {t('fileManager.zipProcessing')}
          </Text>
        )}
        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
          {t('common.mediaPicker.filenameSanitizationNote')}
        </Text>
      </Modal>

      {/* Create Directory Modal */}
      <Modal
        title={t('common.mediaPicker.createDirectory')}
        open={createDirModalVisible}
        onOk={handleCreateDirectory}
        onCancel={() => {
          setCreateDirModalVisible(false);
          setNewDirName('');
        }}
        okText={t('common.create')}
        cancelText={t('common.cancel')}
        okButtonProps={{ disabled: !newDirName.trim() }}
      >
        <Input
          placeholder={t('common.mediaPicker.directoryName')}
          value={newDirName}
          onChange={(e) => setNewDirName(e.target.value)}
          onPressEnter={handleCreateDirectory}
        />
        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
          {t('common.mediaPicker.directorySanitizationNote')}
        </Text>
      </Modal>

      {/* Preview Modal */}
      <Image
        style={{ display: 'none' }}
        src={previewImage}
        preview={{
          visible: previewVisible,
          onVisibleChange: setPreviewVisible,
        }}
      />
    </div>
  );
};

// Export MediaPickerTrigger as default for backward compatibility (used in forms/fields)
export default MediaPickerTrigger;
